import { useEffect, useRef } from "react"
import { useLocation } from "react-router-dom"

import { getApiBaseUrl } from "../shared/lib/createApiClient"

const SESSION_KEY = "sgr_analytics_session"
const EVENT_KEY = "sgr_analytics_event"
const STARTED_KEY = "sgr_analytics_started"
const DEDUPE_MS = 800

const createSessionId = () => {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID().replace(/-/g, "")
    }
    return `s${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`
}

export const getAnalyticsSessionId = () => {
    try {
        let id = localStorage.getItem(SESSION_KEY)
        if (!id || !/^[a-zA-Z0-9_-]+$/.test(id)) {
            id = createSessionId()
            localStorage.setItem(SESSION_KEY, id)
        }
        return id
    } catch (_err) {
        return createSessionId()
    }
}

const getStoredEventId = () => {
    try {
        return sessionStorage.getItem(EVENT_KEY) || null
    } catch (_err) {
        return null
    }
}

const setStoredEvent = (eventId, startedAt) => {
    try {
        if (eventId) sessionStorage.setItem(EVENT_KEY, eventId)
        else sessionStorage.removeItem(EVENT_KEY)
        if (startedAt) sessionStorage.setItem(STARTED_KEY, String(startedAt))
        else sessionStorage.removeItem(STARTED_KEY)
    } catch (_err) {
        // ignore
    }
}

const getStoredStartedAt = () => {
    try {
        const raw = sessionStorage.getItem(STARTED_KEY)
        const n = Number(raw)
        return Number.isFinite(n) ? n : null
    } catch (_err) {
        return null
    }
}

const extractVehicleId = (pathname) => {
    const match = pathname.match(/^\/vehicles\/([a-fA-F0-9]{24})\/?$/)
    return match ? match[1] : null
}

const postJson = (path, payload) => {
    fetch(`${getApiBaseUrl()}${path}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        keepalive: true,
        credentials: "omit",
    }).catch(() => {})
}

const flushDwell = () => {
    const eventId = getStoredEventId()
    const startedAt = getStoredStartedAt()
    if (!eventId || !startedAt) return

    const durationMs = Math.max(0, Date.now() - startedAt)
    postJson("/api/analytics/dwell", {
        eventId,
        sessionId: getAnalyticsSessionId(),
        durationMs,
    })
}

/**
 * Fire-and-forget pageview for marketing routes. Skips admin and ignores failures.
 * Returns a promise that resolves with { eventId } when available.
 */
export const trackPageView = async ({ path, vehicleId, prevEventId, durationMs } = {}) => {
    if (typeof window === "undefined") return null
    if (!path || path.startsWith("/admin")) return null

    const payload = {
        path,
        sessionId: getAnalyticsSessionId(),
        ...(vehicleId ? { vehicleId } : {}),
        ...(prevEventId ? { prevEventId, durationMs } : {}),
    }

    try {
        const response = await fetch(`${getApiBaseUrl()}/api/analytics/pageview`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
            keepalive: true,
            credentials: "omit",
        })
        if (!response.ok) return null
        const data = await response.json().catch(() => null)
        if (data?.eventId) {
            setStoredEvent(data.eventId, Date.now())
            return data
        }
    } catch (_err) {
        // ignore
    }
    return null
}

/**
 * Tracks SPA navigations on the public marketing site, with dwell time.
 */
const PageViewTracker = () => {
    const location = useLocation()
    const lastRef = useRef({ path: "", at: 0 })
    const pageStartedRef = useRef(Date.now())

    useEffect(() => {
        const path = location.pathname || "/"
        if (path.startsWith("/admin")) return undefined

        const now = Date.now()
        if (lastRef.current.path === path && now - lastRef.current.at < DEDUPE_MS) {
            return undefined
        }

        const prevPath = lastRef.current.path
        const prevEventId = getStoredEventId()
        const durationMs = prevPath
            ? Math.max(0, now - (getStoredStartedAt() || pageStartedRef.current))
            : null

        lastRef.current = { path, at: now }
        pageStartedRef.current = now

        trackPageView({
            path,
            vehicleId: extractVehicleId(path),
            ...(prevEventId && durationMs != null ? { prevEventId, durationMs } : {}),
        })

        return undefined
    }, [location.pathname])

    useEffect(() => {
        const onHide = () => {
            if (document.visibilityState === "hidden") flushDwell()
        }
        const onUnload = () => flushDwell()

        document.addEventListener("visibilitychange", onHide)
        window.addEventListener("pagehide", onUnload)
        window.addEventListener("beforeunload", onUnload)

        return () => {
            document.removeEventListener("visibilitychange", onHide)
            window.removeEventListener("pagehide", onUnload)
            window.removeEventListener("beforeunload", onUnload)
            flushDwell()
        }
    }, [])

    return null
}

export default PageViewTracker
